/*
 *  Workspace Overview Widget for Dashboard
 *  Shows running/stopped/total workspace counts with a create shortcut.
 *  Styled to match the modern workspace card design from the Workspaces page.
 */

import React from 'react';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

// Status definitions using RMIT brand colours
const statusDefs = [
  { key: 'running', label: 'Running', icon: 'play', hexColor: '#166534', hexBg: '#f0fdf4', border: '#bbf7d0' },
  { key: 'stopped', label: 'Stopped', icon: 'stop', hexColor: '#c2410c', hexBg: '#fff7ed', border: '#fed7aa' },
  { key: 'pending', label: 'Provisioning', icon: 'spinner', spin: true, hexColor: '#40407F', hexBg: '#ededf5', border: '#B2B2CC' },
  { key: 'errored', label: 'Needs Attention', icon: 'exclamation circle', hexColor: '#E61E2A', hexBg: '#fce8e9', border: '#f5a3a8' },
];

class WorkspaceOverviewWidget extends React.Component {
  getWorkspaceCounts() {
    const store = this.props.scEnvironmentsStore;
    if (!store) return { total: 0, running: 0, stopped: 0, pending: 0, errored: 0 };

    const envs = store.list || [];
    let running = 0;
    let stopped = 0;
    let pending = 0;
    let errored = 0;

    envs.forEach(env => {
      const status = (env.status || '').toUpperCase();
      if (status === 'COMPLETED' || status === 'COMPLETED_EXPIRED') running++;
      else if (status === 'STOPPED') stopped++;
      else if (status === 'PENDING' || status === 'STARTING' || status === 'STOPPING' || status === 'RESTARTING') pending++;
      else if (status === 'FAILED' || status === 'TAINTED' || status === 'STOPPING_FAILED' ||
               status === 'STARTING_FAILED' || status === 'TERMINATING_FAILED' || status === 'INSUFFICIENT_FUNDS') errored++;
    });

    return { total: running + stopped + pending + errored, running, stopped, pending, errored };
  }

  render() {
    const { isLoading, onCreateWorkspace, onViewWorkspaces } = this.props;
    const counts = this.getWorkspaceCounts();

    // Only show statuses that have > 0 count (running/stopped always shown)
    const visibleStatuses = statusDefs.filter(
      s => s.key === 'running' || s.key === 'stopped' || counts[s.key] > 0,
    );

    return (
      <div className="workspace-card" style={{ overflow: 'hidden' }}>
        {/* Accent bar matching workspace cards */}
        <div className="workspace-card-accent" style={{ background: 'var(--rmit-blue)' }} />

        <div style={{ padding: '20px 24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: '600', color: 'var(--rmit-blue)' }}>
                <Icon name="server" style={{ margin: 0, lineHeight: 1, height: '1em', marginRight: '8px', color: 'var(--rmit-blue)' }} />
                Workspaces
              </h3>
              <span
                className="workspace-status-badge"
                style={{ background: '#f3f4f6', color: '#374151', fontSize: '0.75em' }}
              >
                {counts.total} active
              </span>
            </div>
            <span
              style={{ fontSize: '0.8em', color: 'var(--rmit-blue)', cursor: 'pointer', fontWeight: '500' }}
              onClick={onViewWorkspaces}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onViewWorkspaces()}
            >
              View all →
            </span>
          </div>

          {isLoading ? (
            <ProgressPlaceHolder segmentCount={1} />
          ) : (
            <>
              {/* Status cards — uses workspace-info-grid pattern */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${visibleStatuses.length}, 1fr)`,
                gap: '12px',
                marginBottom: '20px',
              }}>
                {visibleStatuses.map(s => (
                  <div
                    key={s.key}
                    style={{
                      background: s.hexBg,
                      borderRadius: '12px',
                      border: `1px solid ${s.border}`,
                      padding: '16px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <Icon
                        name={s.icon}
                        loading={s.spin || false}
                        style={{ color: s.hexColor, margin: 0, lineHeight: 1, height: '1em', fontSize: '0.85em' }}
                      />
                    </div>
                    <div style={{ fontSize: '1.8em', fontWeight: '700', color: s.hexColor, lineHeight: 1 }}>
                      {counts[s.key]}
                    </div>
                    <div style={{ fontSize: '0.75em', fontWeight: '600', color: s.hexColor, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Create workspace action — styled like workspace-action-btn */}
              <button
                onClick={onCreateWorkspace}
                className="workspace-action-btn workspace-action-btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '10px 16px' }}
              >
                <Icon name="plus" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                Create New Workspace
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default observer(WorkspaceOverviewWidget);
